<template>
  <div class="proposal-detail">
    <div
      class="text-6 gray--text d-flex align-center clickable"
      @click="
        $router.push({
          name: 'TendermintGovernance',
        })
      "
    >
      <v-icon small class="gray--text">mdi-chevron-left</v-icon>
      <span class="ml-1">Go Back</span>
    </div>
    <div class="mt-8">
      <div class="d-flex align-center">
        <img
          v-if="$vuetify.theme.dark"
          :src="currentProject.logoSmallDark || currentProject.logoSmall"
          height="36"
          width="36"
        />
        <img v-else :src="currentProject.logoSmall" height="36" width="36" />
        <div class="heading-4 ml-6">{{ currentProject.text }}</div>
        <div class="d-flex">
          <v-divider vertical class="mx-6"></v-divider>
          <div class="heading-4">Proposal Details</div>
        </div>
      </div>
    </div>
    <div class="mt-10" v-if="proposal">
      <div class="d-flex align-baseline justify-space-between">
        <div>
          <div class="d-flex align-center">
            <div class="heading-4">Proposal #{{ proposal.id }}</div>
            <div
              :class="[
                'status ml-5 px-5 rounded-20 text-5 text-uppercase',
                'status-' + proposal.status.number,
              ]"
            >
              {{ proposal.status.text }}
            </div>
          </div>
          <div class="mt-2 text-3">{{ proposal.title }}</div>
        </div>
        <div>
          <c-btn
            class="width-100 mt-2"
            is-short
            v-if="proposal.status.number === 2"
            @click="modalType = 'vote'"
            >Vote</c-btn
          >
          <c-btn
            class="width-100 mt-2"
            is-short
            v-if="proposal.status.number === 1"
            @click="modalType = 'deposit'"
            >Deposit</c-btn
          >
          <c-modal
            :value="!!modalType"
            @onClosed="modalType = null"
            width="687px"
            persistent
          >
            <component
              v-if="modalType"
              :is="modalType"
              :proposal="proposal"
              @closeModal="modalType = null"
            />
          </c-modal>
        </div>
      </div>
      <v-divider class="mt-5 border-stake"></v-divider>
      <v-container class="pa-0 mt-5">
        <v-row class="text-4 mt-2" no-gutters>
          <v-col cols="12" sm="4" lg="3">Proposer</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            <a
              class="text-decoration-none"
              :href="
                `${currentProject.config.explorerAddrLink}${proposal.proposer.proposer}`
              "
              target="_blank"
              rel="noopener noreferrer"
              >{{ proposal.proposer | proposer(currentProject) }}</a
            >
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Min Deposit</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            {{
              proposal.minDeposit
                | denomConverter(currentProject.staking)
                | denomFormat
            }}
            {{ currentProject.staking.token | uppercase }}
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Total Deposit</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            {{
              proposal.totalDeposit
                | denomConverter(currentProject.staking)
                | denomFormat
            }}
            {{ currentProject.staking.token | uppercase }}
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col>Submit Time</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            {{
              new Date(proposal.submitTime) | datetime("yyyy-MM-dd HH:mm:ss")
            }}
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Deposit End Time</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            {{
              new Date(proposal.depositEndTime)
                | datetime("yyyy-MM-dd HH:mm:ss")
            }}
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Voting Start</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            <span v-if="proposal.status.number > 1">
              {{
                new Date(proposal.voteStartTime)
                  | datetime("yyyy-MM-dd HH:mm:ss")
              }}
            </span>
            <span v-else>-</span>
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Voting End</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            <span v-if="proposal.status.number > 1">
              {{
                new Date(proposal.voteEndTime) | datetime("yyyy-MM-dd HH:mm:ss")
              }}
            </span>
            <span v-else>-</span>
          </v-col>
        </v-row>
        <v-row class="text-4 mt-3" no-gutters>
          <v-col cols="12" sm="4" lg="3">Type</v-col>
          <v-col class="darkgray--text" cols="12" sm="8" lg="9">
            {{ proposal.type }}
          </v-col>
        </v-row>
        <v-row no-gutters class="mt-3">
          <v-col cols="12" sm="4" lg="3">Description</v-col>
          <v-col
            class="darkgray--text white-space-pre-line"
            cols="12"
            md="8"
            lg="9"
          >
            <div class="proposal-desc">
              <markdown>{{ proposal.description }}</markdown>
            </div>
            <div class="mt-2">
              <a
                class="text-decoration-none"
                :href="
                  `${currentProject.config.proposalDetailsLink}${proposal.id}`
                "
                target="_blank"
                rel="noopener noreferrer"
              >
                Click for more information.</a
              >
            </div>
          </v-col>
        </v-row>
        <div class="mt-5" v-if="proposal.status.number > 1">
          <v-row class="text-4 mt-3" no-gutters>
            <v-col cols="12" sm="4" lg="3">Total</v-col>
            <v-col class="darkgray--text" cols="12" sm="8" lg="9">
              {{
                proposal.finalTallyResult.total
                  | denomConverter(currentProject.staking)
                  | denomFormat
              }}
              {{ currentProject.staking.token | uppercase }}
            </v-col>
          </v-row>
          <tally-chart :result="proposal.finalTallyResult" height="40px" />
          <v-row class="mt-5" no-gutters>
            <v-col
              v-for="item in proposal.finalTallyResult.details"
              :key="item.key"
              class="d-flex align-center"
              cols="12"
              sm="6"
              md="3"
            >
              <div :class="['indicator', item.key]"></div>
              <div class="black--text ml-3">
                <div class="text-4">
                  {{ item.displayName }}
                </div>
                <div class="text-5 mt-2 darkgray--text">{{ item.rate }}%</div>
                <div class="text-5 mt-2 darkgray--text">
                  {{
                    item.amount
                      | denomConverter(currentProject.staking)
                      | denomFormat
                  }}
                  {{ currentProject.staking.token | uppercase }}
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
    <c-loading v-else />
  </div>
</template>

<script>
import BN from "bignumber.js";
import { mapGetters } from "vuex";
import Markdown from "vue-markdown";
import TallyChart from "./TallyChart.vue";
import Deposit from "./Deposit.vue";
import Vote from "./Vote.vue";

const statusMap = {
  1: "Deposit Period",
  2: "Voting Period",
  3: "Passed",
  4: "Rejected",
};
const statusNameMap = {
  DepositPeriod: 1,
  VotingPeriod: 2,
  ValidatorVotingPeriod: 2,
  Passed: 3,
  Rejected: 4,
  PROPOSAL_STATUS_DEPOSIT_PERIOD: 1,
  PROPOSAL_STATUS_VOTING_PERIOD: 2,
  PROPOSAL_STATUS_VALIDATOR_VOTING_PERIOD: 2,
  PROPOSAL_STATUS_PASSED: 3,
  PROPOSAL_STATUS_REJECTED: 4,
};
const tallyResultMap = {
  yes: {
    displayName: "Yes",
    valueNumber: 1,
  },
  no: {
    displayName: "No",
    valueNumber: 3,
  },
  no_with_veto: {
    displayName: "NoWithVeto",
    valueNumber: 4,
  },
  abstain: {
    displayName: "Abstain",
    valueNumber: 2,
  },
};
export default {
  components: {
    TallyChart,
    Deposit,
    Vote,
    Markdown,
  },
  computed: {
    ...mapGetters({
      currentProject: "projectConfig/latestProject",
    }),
  },
  data: () => ({
    proposal: null,
    tallyResultMap,
    otherInfo: {},
    parameters: null,
    modalType: null,
  }),
  methods: {
    async fetchProposalInfo() {
      const proposalInfo = await this.$store.dispatch(
        "staking/tendermint/fetchProposalInfo",
        this.$route.params.proposalId
      );
      if (!proposalInfo) {
        this.$store.dispatch("addAlert", {
          type: "error",
          title: "Error",
          message: "Could not load proposal. Please try again later.",
        });
        return this.$router.push({
          name: "TendermintGovernance",
        });
      }
      await this.fetchOtherInfo(proposalInfo);
      if ("string" === typeof proposalInfo.status) {
        proposalInfo.status = statusNameMap[proposalInfo.status];
      }
      this.proposal = {
        id: proposalInfo.id,
        type: proposalInfo.content?.type?.split("/")[1] || "--",
        title: proposalInfo.content.value?.title ?? proposalInfo.content.title,
        description:
          proposalInfo.content.value?.description ??
          proposalInfo.content.description,
        finalTallyResult: this.mapFinalTallyResult(this.otherInfo.tallyResult),
        status: {
          number:
            proposalInfo.status || statusNameMap[proposalInfo.proposal_status],
          text:
            statusMap[
              proposalInfo.status || statusNameMap[proposalInfo.proposal_status]
            ],
        },
        depositEndTime: proposalInfo.deposit_end_time,
        submitTime: proposalInfo.submit_time,
        voteStartTime: proposalInfo.voting_start_time,
        voteEndTime: proposalInfo.voting_end_time,
        minDeposit: this.parameters?.deposit?.min_deposit?.[0]?.amount || 0,
        totalDeposit: proposalInfo.total_deposit[0]?.amount ?? 0,
        proposer: this.otherInfo.proposer || {},
      };
    },
    async fetchOtherInfo(proposalInfo) {
      const [proposer, parameters, tallyResult] = await Promise.all([
        this.$store.dispatch(
          "staking/tendermint/fetchProposer",
          this.$route.params.proposalId
        ),
        this.$store.dispatch("staking/tendermint/fetchProposalParameters"),
        new Promise(async resolve => {
          if (
            BN.sum(
              ...Object.values(proposalInfo.final_tally_result)
            ).isGreaterThan(0)
          ) {
            return resolve(proposalInfo.final_tally_result);
          }
          const result = await this.$store.dispatch(
            "staking/tendermint/fetchProposalTally",
            this.$route.params.proposalId
          );
          return resolve(result);
        }),
      ]);
      this.otherInfo = {
        ...this.otherInfo,
        proposer,
        tallyResult,
      };
      this.parameters = parameters;
    },
    mapFinalTallyResult(result = {}) {
      const total = Object.values(result).reduce(
        (acc, cur) => new BN(acc).plus(cur).toNumber(),
        0
      );
      const mostValue = Math.max(...Object.values(result));
      const resultItems = Object.keys(tallyResultMap).map(key => ({
        key,
        displayName: tallyResultMap[key].displayName,
        optionValue: this.currentProject.staking.gov.vote.useOptNumber
          ? tallyResultMap[key].valueNumber
          : tallyResultMap[key].displayName,
        amount: result[key] || 0,
        rate: !total
          ? 0
          : new BN(result[key] || 0)
              .dividedBy(total)
              .multipliedBy(100)
              .toFixed(2, BN.ROUND_HALF_FLOOR),
        mostVoted: total ? result[key] == mostValue : false,
      }));
      return {
        total,
        details: resultItems,
        mostVoted: resultItems.find(item => item.mostVoted) || resultItems[0],
      };
    },
  },
  filters: {
    proposer(value) {
      return value.moniker || value.proposer || "";
    },
  },
  mounted() {
    this.fetchProposalInfo();
  },
};
</script>

<style lang="scss">
.proposal-detail {
  .status {
    height: 30px;
    display: flex;
    align-items: center;
  }
  .status-1 {
    color: var(--v-warning-base);
    background-color: rgb(255, 178, 30, 0.1);
  }
  .status-2 {
    color: var(--v-info-base);
    background-color: rgb(33, 150, 243, 0.1);
  }
  .status-3 {
    color: var(--v-green-base);
    background-color: rgb(74, 216, 152, 0.1);
  }
  .status-4 {
    color: var(--v-error-base);
    background-color: rgb(255, 32, 32, 0.1);
  }
  .indicator {
    width: 4px;
    height: 100%;
  }
  .yes {
    background-color: var(--v-green-base);
  }
  .no {
    background-color: rgb(238, 103, 103);
  }
  .abstain {
    background-color: var(--gray);
  }
  .no_with_veto {
    background-color: var(--purple);
  }
  .proposal-desc {
    max-height: 300px;
    overflow: auto;
    padding: 16px;
    background-color: var(--lightgray);
    border-radius: 8px;
    h1 {
      font-size: 24px !important;
    }
    h2 {
      font-size: 20px !important;
    }
    h3 {
      font-size: 18px !important;
    }
  }
}
</style>
